import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useSupabaseAuth } from '@/context/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, Save, RefreshCw } from 'lucide-react';
import { clearCachedIpLocation, readCachedIpLocation, writeCachedIpLocation } from '@/services/locationCache';

interface LocationData {
  country: string;
  state_province: string;
  latitude: number | null;
  longitude: number | null;
  auto_location_enabled: boolean;
}

const LocationSettings: React.FC = () => {
  const { user } = useSupabaseAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const didAutoDetectRef = useRef(false);
  const [locationData, setLocationData] = useState<LocationData>({
    country: '',
    state_province: '',
    latitude: null,
    longitude: null,
    auto_location_enabled: true,
  });

  // Fetch current location data
  useEffect(() => {
    if (!user) return;

    const fetchLocationData = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('country, state_province, latitude, longitude, auto_location_enabled')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data) {
          setLocationData({
            country: (data as any).country || '',
            state_province: (data as any).state_province || '',
            latitude: (data as any).latitude,
            longitude: (data as any).longitude,
            auto_location_enabled: (data as any).auto_location_enabled ?? true,
          });
        }
      } catch (error) {
        console.error('Error fetching location data:', error);
      }
    };

    fetchLocationData();
  }, [user]);

  // If auto-location is enabled, try to auto-detect once on load
  useEffect(() => {
    if (!user) return;
    if (didAutoDetectRef.current) return;
    if (!locationData.auto_location_enabled) return;

    // Only auto-detect if user hasn't set anything yet
    const hasAnyLocation =
      !!locationData.country ||
      !!locationData.state_province ||
      (locationData.latitude != null && locationData.longitude != null);

    if (hasAnyLocation) return;

    didAutoDetectRef.current = true;
    // Fire-and-forget; errors are handled inside detectLocation()
    void detectLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, locationData.auto_location_enabled, locationData.country, locationData.state_province, locationData.latitude, locationData.longitude]);

  // Auto-detect location
  const detectLocation = async () => {
    setIsLoading(true);
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser');
      }

      // Geolocation requires a secure context (https or localhost)
      if (!window.isSecureContext) {
        throw new Error('Geolocation requires HTTPS (or localhost).');
      }

      // If the Permissions API is available, surface "denied" immediately with a clearer message.
      // (Some browsers/plugins return opaque POSITION_UNAVAILABLE errors otherwise.)
      try {
        const anyNavigator = navigator as any;
        const perm = await anyNavigator?.permissions?.query?.({ name: 'geolocation' });
        if (perm?.state === 'denied') {
          throw new Error('Location permission is blocked for this site.');
        }
      } catch {
        // Ignore Permissions API failures; we’ll still attempt geolocation.
      }

      const getPosition = (options: PositionOptions) =>
        new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });

      // Try high-accuracy first (GPS), then fall back to network-based positioning.
      let position: GeolocationPosition | null = null;
      try {
        position = await getPosition({
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        });
      } catch (err: any) {
        // Some environments return POSITION_UNAVAILABLE with non-standard messages (e.g. "Response was malformed").
        // Falling back to non-high-accuracy can succeed in those cases.
        position = await getPosition({
          enableHighAccuracy: false,
          timeout: 20000,
          maximumAge: 0,
        });
      }

      const { latitude, longitude } = position.coords;

      // Use reverse geocoding to get location details (best-effort)
      let state = '';
      let country = '';
      try {
        const response = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
        );
        if (response.ok) {
          const data = await response.json();
          state = data?.principalSubdivision || data?.principalSubdivisionCode || '';
          country = data?.countryName || '';
        }
      } catch {
        // Ignore reverse-geocode failures; we’ll still save coordinates.
      }

      setLocationData(prev => ({
        ...prev,
        country,
        state_province: state,
        latitude,
        longitude,
      }));

      toast({
        title: 'Location detected',
        description:
          state || country
            ? `Updated to ${state ? `${state}, ` : ''}${country}`
            : `Saved coordinates (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
      });
    } catch (error) {
      console.error('Error detecting location:', error);

      const err = error as any;
      const isGeoError =
        typeof err?.code === 'number' &&
        (err.code === 1 || err.code === 2 || err.code === 3);

      // Fallback: if browser geolocation is unavailable, approximate using IP-based geolocation.
      // This won’t provide precise coordinates, but it prevents the feature from “always failing”.
      if (isGeoError && err.code === 2) {
        try {
          const cached = readCachedIpLocation();
          if (cached && (cached.region || cached.country || (cached.latitude != null && cached.longitude != null))) {
            setLocationData((prev) => ({
              ...prev,
              country: cached.country || prev.country,
              state_province: cached.region || prev.state_province,
              latitude: cached.latitude,
              longitude: cached.longitude,
            }));

            toast({
              title: 'Location approximated',
              description:
                cached.region || cached.country
                  ? `Using cached IP-based location: ${cached.region ? `${cached.region}, ` : ''}${cached.country || 'Unknown'}`
                  : 'Using cached IP-based location (coordinates only).',
            });
            return;
          }

          // ipapi.co is widely used and returns lat/lng + region/country in one call.
          const resp = await fetch('https://ipapi.co/json/');
          if (!resp.ok) throw new Error('IP lookup failed');
          const ipData = await resp.json();

          const ipLat =
            typeof ipData?.latitude === 'number'
              ? ipData.latitude
              : ipData?.latitude
                ? Number(ipData.latitude)
                : null;
          const ipLng =
            typeof ipData?.longitude === 'number'
              ? ipData.longitude
              : ipData?.longitude
                ? Number(ipData.longitude)
                : null;

          const ipState = ipData?.region || '';
          const ipCountry = ipData?.country_name || '';

          if (ipState || ipCountry || (ipLat != null && ipLng != null)) {
            writeCachedIpLocation({
              country: ipCountry,
              region: ipState,
              latitude: ipLat,
              longitude: ipLng,
            });

            setLocationData((prev) => ({
              ...prev,
              country: ipCountry || prev.country,
              state_province: ipState || prev.state_province,
              latitude: ipLat,
              longitude: ipLng,
            }));

            toast({
              title: 'Location approximated',
              description:
                ipState || ipCountry
                  ? `Using IP-based location: ${ipState ? `${ipState}, ` : ''}${ipCountry || 'Unknown'}`
                  : 'Using IP-based location (coordinates only).',
            });
            return;
          }
        } catch (ipErr) {
          // ignore and fall through to user-facing error toast
        }
      }

      const detail = isGeoError
        ? err.code === 1
          ? 'Permission denied. Please allow location access in your browser settings.'
          : err.code === 2
            ? 'Location unavailable. On Linux this is often caused by the system location service (GeoClue) failing; try enabling location services in your OS settings or in the browser, then retry.'
            : 'Timed out while trying to get your location. Please try again.'
        : err?.message || 'Please enter your location manually or check your browser permissions.';

      toast({
        title: 'Location detection failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save location data
  const saveLocationData = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const { error } = await (supabase
        .from('profiles')
        .update({
          country: locationData.country,
          state_province: locationData.state_province,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          auto_location_enabled: locationData.auto_location_enabled,
          location_last_updated: new Date().toISOString(),
        } as any) as any)
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: 'Location saved',
        description: 'Your location preferences have been updated.',
      });

      // User explicitly saved a location; clear any stale IP-cache.
      clearCachedIpLocation();
    } catch (error) {
      console.error('Error saving location data:', error);
      toast({
        title: 'Error saving location',
        description: 'Failed to save your location preferences.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="group border-border/40 bg-card/60 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
      <div className="absolute inset-0 bg-gradient-to-br from-[#40e0d0]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
      <CardHeader className="relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#40e0d0]/10 flex items-center justify-center">
              <MapPin className="h-5 w-5 text-[#40e0d0]" />
            </div>
            <div>
              <CardTitle className="text-xl">Location Settings</CardTitle>
              <p className="text-sm text-muted-foreground">
                Set your location for relevant environmental content and regional updates
              </p>
            </div>
          </div>
          {(locationData.country || locationData.state_province) && (
            <Badge variant="outline" className="bg-[#40e0d0]/10 text-[#40e0d0] border-[#40e0d0]/30">
              <MapPin className="h-3 w-3 mr-1" />
              {locationData.state_province && locationData.country 
                ? `${locationData.state_province}, ${locationData.country}`
                : locationData.country || locationData.state_province}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="relative space-y-8">
        {/* Enhanced Auto-location Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl border border-border/40 bg-background/50">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <Label htmlFor="auto-location" className="text-sm font-medium cursor-pointer">
                  Auto-detect location
                </Label>
                <p className="text-xs text-muted-foreground">
                  Automatically update location when you visit the site
                </p>
              </div>
            </div>
            <Switch
              id="auto-location"
              checked={locationData.auto_location_enabled}
              onCheckedChange={(checked) =>
                setLocationData(prev => ({ ...prev, auto_location_enabled: checked }))
              }
              className="data-[state=checked]:bg-[#40e0d0]"
            />
          </div>
        </div>

        {/* Enhanced Manual Location Input */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <div>
              <Label className="text-sm font-medium">Manual Location</Label>
              <p className="text-xs text-muted-foreground">
                Set your location manually for personalized content
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Label htmlFor="country" className="text-sm font-medium flex items-center gap-2">
                <svg className="h-4 w-4 text-[#40e0d0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Country
              </Label>
              <Input
                id="country"
                value={locationData.country}
                onChange={(e) =>
                  setLocationData(prev => ({ ...prev, country: e.target.value }))
                }
                placeholder="Enter your country"
                className="bg-background/50 border-border/40 focus:border-[#40e0d0] transition-colors"
              />
            </div>
            
            <div className="space-y-3">
              <Label htmlFor="state-province" className="text-sm font-medium flex items-center gap-2">
                <svg className="h-4 w-4 text-[#40e0d0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                State/Province
              </Label>
              <Input
                id="state-province"
                value={locationData.state_province}
                onChange={(e) =>
                  setLocationData(prev => ({ ...prev, state_province: e.target.value }))
                }
                placeholder="Enter your state or province"
                className="bg-background/50 border-border/40 focus:border-[#40e0d0] transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Enhanced Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            variant="outline"
            onClick={detectLocation}
            disabled={isLoading}
            className="flex-1 gap-3 h-12 bg-background/50 border-[#40e0d0]/30 hover:border-[#40e0d0] hover:bg-[#40e0d0]/5 transition-all"
          >
            {isLoading ? (
              <RefreshCw className="h-5 w-5 animate-spin text-[#40e0d0]" />
            ) : (
              <div className="h-8 w-8 rounded-lg bg-[#40e0d0]/10 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-[#40e0d0]" />
              </div>
            )}
            <div className="text-left flex-1">
              <div className="font-medium">
                {isLoading ? 'Detecting Location...' : 'Detect Current Location'}
              </div>
              <div className="text-xs text-muted-foreground">
                {isLoading ? 'Using GPS and network' : 'Use device GPS'}
              </div>
            </div>
          </Button>
          
          <Button
            onClick={saveLocationData}
            disabled={isSaving}
            className="flex-1 gap-3 h-12 bg-[#40e0d0] hover:bg-[#40e0d0]/90 text-black font-medium"
          >
            {isSaving ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Save className="h-4 w-4" />
              </div>
            )}
            <div className="text-left flex-1">
              <div className="font-medium">
                {isSaving ? 'Saving...' : 'Save Location'}
              </div>
              <div className="text-xs opacity-80">
                {isSaving ? 'Updating preferences' : 'Apply changes'}
              </div>
            </div>
          </Button>
        </div>

        {/* Location Coordinates Display */}
        {(locationData.latitude && locationData.longitude) && (
          <div className="rounded-lg border border-border/40 bg-muted/30 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium">Coordinates</div>
                  <div className="text-xs text-muted-foreground">
                    {locationData.latitude.toFixed(4)}, {locationData.longitude.toFixed(4)}
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                GPS Enabled
              </Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LocationSettings;
